import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const AdminProjects = () => {
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title_fr: '',
    title_en: '',
    description_fr: '',
    description_en: '',
    category_fr: '',
    category_en: '',
    date: '',
    location_fr: '',
    location_en: '',
    image: '',
    achievements_fr: ['', '', ''],
    achievements_en: ['', '', '']
  });

  // Simple authentication - Admin password check
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');

  const ADMIN_PASSWORD = 'prestaco2025'; // Simple password - should be env variable in production

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Connexion réussie');
    } else {
      toast.error('Mot de passe incorrect');
    }
  };

  const fetchProjects = useCallback(async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/projects`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Erreur lors du chargement des projets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProjects();
    }
  }, [isAuthenticated, fetchProjects]);

  const toggleAddForm = useCallback(() => {
    console.log('Toggle button clicked, current state:', showAddForm);
    setShowAddForm(prev => {
      console.log('Setting showAddForm to:', !prev);
      return !prev;
    });
  }, [showAddForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = editingId 
        ? `${backendUrl}/api/projects/${editingId}`
        : `${backendUrl}/api/projects`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingId ? 'Projet modifié avec succès' : 'Projet ajouté avec succès');
        fetchProjects();
        resetForm();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/projects/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Projet supprimé avec succès');
        fetchProjects();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (project) => {
    setFormData({
      title_fr: project.title_fr,
      title_en: project.title_en,
      description_fr: project.description_fr,
      description_en: project.description_en,
      category_fr: project.category_fr,
      category_en: project.category_en,
      date: project.date,
      location_fr: project.location_fr,
      location_en: project.location_en,
      image: project.image,
      achievements_fr: project.achievements_fr,
      achievements_en: project.achievements_en
    });
    setEditingId(project.id);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      title_fr: '',
      title_en: '',
      description_fr: '',
      description_en: '',
      category_fr: '',
      category_en: '',
      date: '',
      location_fr: '',
      location_en: '',
      image: '',
      achievements_fr: ['', '', ''],
      achievements_en: ['', '', '']
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const updateAchievement = (lang, index, value) => {
    const key = `achievements_${lang}`;
    const newAchievements = [...formData[key]];
    newAchievements[index] = value;
    setFormData({ ...formData, [key]: newAchievements });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Admin - PRESTACO
          </h1>
          <p className="text-gray-600 mb-6 text-center">
            Gestion des Réalisations
          </p>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            />
            <Button onClick={handleLogin} className="w-full bg-blue-600 hover:bg-blue-700">
              Se connecter
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pt-24 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">
              Gestion des Réalisations
            </h1>
            <p className="text-gray-600 mt-2">Interface d'administration PRESTACO</p>
          </div>
          <Button
            onClick={toggleAddForm}
            type="button"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            {showAddForm ? 'Masquer le formulaire' : 'Ajouter une réalisation'}
          </Button>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="p-6 mb-8 border-2 border-blue-200">
            <h2 className="text-2xl font-bold mb-6">
              {editingId ? 'Modifier la réalisation' : 'Nouvelle réalisation'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Titre (FR)</label>
                  <Input
                    required
                    value={formData.title_fr}
                    onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Title (EN)</label>
                  <Input
                    required
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Description (FR)</label>
                  <Textarea
                    required
                    value={formData.description_fr}
                    onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description (EN)</label>
                  <Textarea
                    required
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Catégorie (FR)</label>
                  <Input
                    required
                    value={formData.category_fr}
                    onChange={(e) => setFormData({ ...formData, category_fr: e.target.value })}
                    placeholder="Informatique"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Category (EN)</label>
                  <Input
                    required
                    value={formData.category_en}
                    onChange={(e) => setFormData({ ...formData, category_en: e.target.value })}
                    placeholder="IT Equipment"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date</label>
                  <Input
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="2024"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Localisation (FR)</label>
                  <Input
                    required
                    value={formData.location_fr}
                    onChange={(e) => setFormData({ ...formData, location_fr: e.target.value })}
                    placeholder="Dakar, Sénégal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Location (EN)</label>
                  <Input
                    required
                    value={formData.location_en}
                    onChange={(e) => setFormData({ ...formData, location_en: e.target.value })}
                    placeholder="Dakar, Senegal"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">URL de l'image</label>
                <Input
                  required
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              {/* Achievements */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Réalisations (FR)</label>
                  {formData.achievements_fr.map((ach, idx) => (
                    <Input
                      key={idx}
                      className="mb-2"
                      value={ach}
                      onChange={(e) => updateAchievement('fr', idx, e.target.value)}
                      placeholder={`Réalisation ${idx + 1}`}
                    />
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Achievements (EN)</label>
                  {formData.achievements_en.map((ach, idx) => (
                    <Input
                      key={idx}
                      className="mb-2"
                      value={ach}
                      onChange={(e) => updateAchievement('en', idx, e.target.value)}
                      placeholder={`Achievement ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  <Save className="mr-2 h-4 w-4" />
                  {editingId ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="mr-2 h-4 w-4" />
                  Annuler
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Projects List */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Projets existants ({projects.length})
          </h2>
          
          {isLoading ? (
            <p>Chargement...</p>
          ) : projects.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-600">Aucun projet. Ajoutez-en un !</p>
            </Card>
          ) : (
            projects.map((project) => (
              <Card key={project.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      {project.image && (
                        <img 
                          src={project.image} 
                          alt={project.title_fr}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{project.title_fr}</h3>
                        <p className="text-sm text-gray-600">{project.category_fr} • {project.date}</p>
                        <p className="text-sm text-gray-500">{project.location_fr}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-2">{project.description_fr}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.achievements_fr?.map((achievement, idx) => (
                        <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {achievement}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(project)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(project.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProjects;
